import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { __PDND_API_URL__ } from 'shell/constants/environment';
import Table from 'shell/components/shared/table';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList, HttpResponseListPayloadQuarkus } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { useFormik } from 'formik';
import { transformDataset } from './utils';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { headCells, validationSchemaFilter } from './constants';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { transformerResponse } from './utils';
import { IconButton, Typography } from '@mui/material';
import FilterNotificationManagementForm from 'practice/components/notification-management/search';
import {
  FiltersNotificationManagementVM,
  NotificationManagementVM,
} from 'practice/view-models/notification-management';
import PmiTitle from 'shell/components/shared/title';
import { noop } from 'shell/utils/noop';
import { SearchNotificationCrumbs } from 'practice/constants/notification-management.crumbs';
import { Visibility } from '@mui/icons-material';
import { generatePath, useNavigate } from 'react-router-dom';
import { initialValuesNotificationManagement } from '..';
import { PDNDNotification } from 'shell/models/pdnd-notification';

const NotificationManagementList = (): React.ReactElement => {
  const totalElementsRef = useRef<number>(0);

  const [notifications, getNotifications] = useHttpFetch<
    HttpResponseListPayloadQuarkus<PDNDNotification>,
    Array<NotificationManagementVM>
  >(`${__PDND_API_URL__}/notifica/list`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: [],
    },
    transformer: (response) => {
      totalElementsRef.current = response.data?.pageInfo?.totalCount || 0;
      return transformerResponse(response);
    },
  });

  const filterTable = useFilterTable<FiltersNotificationManagementVM>();

  const onSubmit = useCallback(
    (formValues: Partial<FiltersNotificationManagementVM>) => {
      const queryString = transformDataset({
        ...formValues,
        page: 0,
      } as FiltersNotificationManagementVM);
      filterNotificationManagementForm.setFieldValue('page', 0);
      getNotifications({
        params: {},
        paramsSerializer: () => queryString,
      });
    },
    [getNotifications]
  );

  const filterNotificationManagementForm = useFormik<Partial<FiltersNotificationManagementVM>>({
    initialValues: initialValuesNotificationManagement,
    validationSchema: validationSchemaFilter,
    onSubmit,
  });

  useEffect(() => {
    filterNotificationManagementForm.setValues(
      (filterTable.values as Partial<FiltersNotificationManagementVM>) ||
        initialValuesNotificationManagement
    );
    const queryString = transformDataset(
      (filterTable.values as FiltersNotificationManagementVM) || initialValuesNotificationManagement
    );
    getNotifications({
      params: {},
      paramsSerializer: () => queryString,
    });
  }, []);

  useEffect(() => {
    return () =>
      filterTable.setValues(
        filterNotificationManagementForm.values as FiltersNotificationManagementVM
      );
  }, [filterNotificationManagementForm.values]);

  const count: number = useMemo(() => {
    return totalElementsRef.current || 0;
  }, [notifications.data]);

  const handleChangePage = useCallback(
    (newPage) => {
      const newDataset = { ...filterNotificationManagementForm.values, page: newPage || 0 };
      const queryString = transformDataset(newDataset);
      (
        getNotifications({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<PDNDNotification>, Array<NotificationManagementVM>>
      ).then(() => {
        filterNotificationManagementForm.setFieldValue('page', newPage);
      });
    },
    [
      getNotifications,
      filterNotificationManagementForm.setFieldValue,
      filterNotificationManagementForm.values,
    ]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...filterNotificationManagementForm.values,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };
      const queryString = transformDataset(newDataset);
      (
        getNotifications({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<PDNDNotification>, Array<NotificationManagementVM>>
      ).then(() => {
        filterNotificationManagementForm.setValues(newDataset);
      });
    },
    [filterNotificationManagementForm.setFieldValue, filterNotificationManagementForm.values]
  );

  const navigate = useNavigate();

  const onVisibilityPress = useCallback((row: NotificationManagementVM) => {
    navigate(generatePath('/pratiche/notifiche/dettaglio/:id', { id: row.id?.toString() || '' }));
  }, []);

  const data = useMemo(() => {
    const castedData = (notifications.data as Array<NotificationManagementVM>) || [];
    return castedData.map((el) => ({
      ...el,
      action: (
        <Fragment>
          <IconButton onClick={() => onVisibilityPress(el)}>
            <Visibility />
          </IconButton>
        </Fragment>
      ),
    }));
  }, [notifications.data as Array<NotificationManagementVM>]);

  return (
    <Fragment>
      <Breadcrumbs items={SearchNotificationCrumbs} />
      <PmiTitle title="Ricerca Comunicazioni SEND" />
      <div className="pmi-my-3">
        <FilterNotificationManagementForm
          filterNotificationForm={filterNotificationManagementForm}
        />
      </div>
      {notifications.data ? (
        data?.length > 0 ? (
          <>
            {/* <CsvExtraction onClick={noop} /> */}
            <Table
              onSort={noop}
              head={headCells.filter((el) => el.id !== 'cf')}
              orders={filterNotificationManagementForm.values.orders || {}}
              page={filterNotificationManagementForm.values.page || 0}
              rowsPerPage={filterNotificationManagementForm.values.rowsPerPage}
              body={data || []}
              count={count || 0}
              isServer
              noSort
              hideButtons
              isNotLayoutFixed
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ariaLabel="Lista Notifiche"
            />
          </>
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

NotificationManagementList.displayName = 'Notification Management List';

export default NotificationManagementList;
